import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Skeleton } from "@mui/material";
import { PlayCircleOutlineRounded } from "@mui/icons-material";

import { useScreen } from "Screen/ScreenContext";

import { Text } from "@fragments";
import { UserAvatar } from "@components/Community";

import api from "@helpers/api";

import "./styles.scss";


function formatDuration(seconds) {
    if (!seconds) return "0:00";
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
}

function resolveBanner(banner) {
    if (!banner) return null;
    if (typeof banner === "string") return banner;
    return banner.condensed ?? banner.original ?? null;
}

const TITLE_TOP_THRESHOLD = 200;
const SECTION_BOTTOM_RATIO = 0.3;

export default function SuggestionCard({ communityId, headerRef, sectionRef }) {
    const navigate = useNavigate();
    const { bodyRef } = useScreen();

    const [suggestion, setSuggestion] = useState(null);
    const [loading, setLoading] = useState(true);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        let cancelled = false;

        async function load() {
            if (!communityId) return;
            setLoading(true);
            try {
                const { data } = await api.get(`community/${communityId}/content/suggestion`);
                if (cancelled) return;
                setSuggestion(data && data.id ? data : null);
            } catch (e) {
                console.log(e);
                if (!cancelled) setSuggestion(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();
        return () => { cancelled = true; };
    }, [communityId]);

    useEffect(() => {
        const body = bodyRef?.current;
        if (!body) return;

        let frameId = null;

        const update = () => {
            frameId = null;
            const header = headerRef?.current;
            const section = sectionRef?.current;
            if (!header || !section) {
                setVisible(false);
                return;
            }

            const bodyRect = body.getBoundingClientRect();
            const headerRect = header.getBoundingClientRect();
            const sectionRect = section.getBoundingClientRect();

            const titleTop = headerRect.top - bodyRect.top;
            const sectionBottom = sectionRect.bottom - bodyRect.top;
            const bodyHeight = bodyRect.height;

            const isMobile = window.innerWidth <= 960;
            const exitThreshold = isMobile ? 0 : SECTION_BOTTOM_RATIO * bodyHeight;

            const inInterval =
                titleTop < TITLE_TOP_THRESHOLD &&
                sectionBottom > exitThreshold;

            setVisible(inInterval);
        };

        const schedule = () => {
            if (frameId == null) frameId = requestAnimationFrame(update);
        };

        update();
        body.addEventListener("scroll", schedule, { passive: true });

        const ro = new ResizeObserver(schedule);
        ro.observe(body);
        if (headerRef?.current) ro.observe(headerRef.current);
        if (sectionRef?.current) ro.observe(sectionRef.current);

        return () => {
            body.removeEventListener("scroll", schedule);
            ro.disconnect();
            if (frameId != null) cancelAnimationFrame(frameId);
        };
    }, [bodyRef, headerRef, sectionRef]);

    if (loading) {
        return (
            <div
                className={`suggestion-card suggestion-card--floater suggestion-card--skeleton${visible ? " is-visible" : ""}`}
                aria-busy="true"
                aria-hidden={!visible}
            >
                <div className="suggestion-card-backdrop suggestion-card-backdrop--placeholder" aria-hidden="true" />
                <div className="suggestion-card-scrim" aria-hidden="true" />

                <div className="suggestion-card-body">
                    <Skeleton
                        variant="rectangular"
                        animation="wave"
                        className="suggestion-thumbnail-skeleton"
                    />
                    <div className="suggestion-info">
                        <Skeleton
                            variant="text"
                            animation="wave"
                            width="55%"
                            height={12}
                            className="suggestion-skeleton-line suggestion-skeleton-label"
                        />
                        <Skeleton
                            variant="text"
                            animation="wave"
                            width="92%"
                            height={18}
                            className="suggestion-skeleton-line"
                        />
                        <Skeleton
                            variant="text"
                            animation="wave"
                            width="70%"
                            height={18}
                            className="suggestion-skeleton-line"
                        />
                        <div className="suggestion-skeleton-creator">
                            <Skeleton
                                variant="circular"
                                animation="wave"
                                width={18}
                                height={18}
                            />
                            <Skeleton
                                variant="text"
                                animation="wave"
                                width={88}
                                height={14}
                                className="suggestion-skeleton-line"
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!suggestion) return null;

    const banner = resolveBanner(suggestion.banner);
    const sectionId = suggestion.section?.id ?? suggestion.section_id ?? suggestion.section;
    const duration = formatDuration(suggestion.video?.duration);
    const aspectRatio = suggestion.video?.aspect_ratio || "16/9";

    const handleOpen = () => {
        if (!visible) return;
        if (!sectionId || !suggestion.id) return;
        navigate(`section/${sectionId}/${suggestion.id}`);
    };

    return (
        <div
            className={`suggestion-card suggestion-card--floater${visible ? " is-visible" : ""}`}
            onClick={handleOpen}
            aria-hidden={!visible}
        >
            {banner && (
                <div className="suggestion-card-backdrop" aria-hidden="true">
                    <img src={banner} alt="" />
                </div>
            )}
            <div className="suggestion-card-scrim" aria-hidden="true" />

            <div className="suggestion-card-body">
                <div
                    className="suggestion-thumbnail"
                    style={{ aspectRatio }}
                >
                    {banner
                        ? <img src={banner} alt={suggestion.name} />
                        : <div className="suggestion-thumbnail-placeholder" />
                    }
                    <div className="suggestion-thumbnail-overlay">
                        <PlayCircleOutlineRounded className="suggestion-thumbnail-icon" />
                    </div>
                </div>

                <div className="suggestion-info">
                    <Text.small
                        value={`Assista em sequência · ${duration}`}
                        contrast={5}
                        weight={300}
                        className="suggestion-label"
                    />
                    <Text.h5
                        value={suggestion.name}
                        contrast={9}
                        weight={400}
                        className="suggestion-title"
                        maxLines={2}
                    />
                    {suggestion.creator?.id && (
                        <div className="suggestion-creator">
                            <UserAvatar size={18} user={suggestion.creator} displayName={true} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
